import { useState } from 'react';
import { MapPin, Calendar, Car, Phone } from 'lucide-react';

import { Button } from '@/components/ui/button';

import heroCar from '@/assets/premium-hero-car.png';

const PHONE_NUMBER = '+919633210606';

const vehicles = [
  { value: '', label: 'Select vehicle type' },
  { value: 'etios', label: 'Etios' },
  { value: 'innova', label: 'Innova' },
  { value: 'innova-crysta', label: 'Innova Crysta' },
  { value: 'tempo-traveler', label: 'Tempo Traveler' },
  { value: 'urbania', label: 'Urbania' },
  { value: 'tourist-bus', label: 'Tourist Bus' },
];

const HeroSection = () => {
  const [pickup, setPickup] = useState('');
  const [destination, setDestination] = useState('');
  const [dateTime, setDateTime] = useState('');
  const [phone, setPhone] = useState('');
  const [vehicleType, setVehicleType] = useState('');

  // Format datetime from YYYY-MM-DDTHH:MM to DD-MM-YYYY HH:MM
  const formatDateTime = (dateTimeStr: string) => {
    if (!dateTimeStr) return 'Not specified';

    const date = new Date(dateTimeStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${day}-${month}-${year} ${hours}:${minutes}`;
  };

  const handleWhatsAppClick = () => {
    const selectedVehicle = vehicles.find(v => v.value === vehicleType)?.label || 'Not specified';
    const formattedDateTime = formatDateTime(dateTime);
    const message = `Hello, I want to book a taxi from Friends On Road Cab, Kannur.

Pickup: ${pickup || 'Not specified'}
Destination: ${destination || 'Not specified'}
Date & Time: ${formattedDateTime}
Vehicle Type: ${selectedVehicle}
Phone: ${phone || 'Not specified'}`;

    window.open(
      `https://wa.me/${PHONE_NUMBER.replace(/\+/g, '')}?text=${encodeURIComponent(message)}`,
      '_blank'
    );
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center pt-16 overflow-hidden"
    >
      {/* Premium Background */}
      <div className="absolute inset-0 bg-slate-950">
        {/* Car Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-80"
          style={{ backgroundImage: `url(${heroCar})` }}
        />
        {/* Gradients & Overlays for Text Readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-slate-950/80" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/50 to-transparent" />
        <div className="absolute inset-0 hero-pattern opacity-30 mix-blend-overlay" />
      </div>

      {/* Content */}
      <div className="container relative z-10 px-4 sm:px-6 lg:px-8 pt-12 sm:pt-6 pb-12">
        <div className="grid lg:grid-cols-2 gap-2 lg:gap-16 xl:gap-20 items-center max-w-7xl mx-auto">
          {/* Hero Text - Left on Desktop */}
          <div className="text-left mb-8 lg:mb-0">
            {/* Main Heading */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3 animate-fade-up leading-tight">
              Friends On Road Cab
            </h1>
            <p className="text-xl sm:text-2xl font-bold gradient-text-animated mb-2 animate-fade-up stagger-1">
              Kannur, Kerala
            </p>

            {/* Malayalam Tagline */}
            <p className="text-sm sm:text-base text-white/70 italic mb-6 animate-fade-up stagger-2">
              "നിങ്ങളുടെ വിശ്വസനീയ യാത്രാ പങ്കാളി"
            </p>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-2 sm:gap-3 justify-start mb-0 animate-fade-up stagger-3">
              <span className="inline-flex items-center gap-1.5 text-xs sm:text-sm bg-white/10 backdrop-blur-sm text-white px-3 sm:px-4 py-2 rounded-full border border-white/20 hover:bg-white/20 hover:border-primary/50 transition-all duration-300 cursor-default">
                <span className="text-primary">✓</span> Local Drivers
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs sm:text-sm bg-white/10 backdrop-blur-sm text-white px-3 sm:px-4 py-2 rounded-full border border-white/20 hover:bg-white/20 hover:border-primary/50 transition-all duration-300 cursor-default">
                <span className="text-primary">✓</span> Clean Vehicles
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs sm:text-sm bg-white/10 backdrop-blur-sm text-white px-3 sm:px-4 py-2 rounded-full border border-white/20 hover:bg-white/20 hover:border-primary/50 transition-all duration-300 cursor-default">
                <span className="text-primary">✓</span> Affordable Rates
              </span>
            </div>
          </div>

          {/* Booking Section - Responsive Card */}
          <div className="max-w-md mx-auto lg:max-w-lg lg:mr-0 animate-scale-in stagger-2 w-full">
            <div className="glass-premium rounded-2xl sm:rounded-3xl p-5 sm:p-7 hover:border-primary/30 transition-all duration-500">
              <div className="space-y-4 sm:space-y-5">
                {/* Pickup Location */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-white mb-2">
                    <MapPin className="h-4 w-4 text-primary" />
                    Pickup Location
                  </label>
                  <input
                    type="text"
                    placeholder="Enter pickup location"
                    value={pickup}
                    onChange={(e) => setPickup(e.target.value)}
                    className="w-full px-4 py-3.5 rounded-xl bg-white/5 border border-white/20 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all duration-300 text-white placeholder-white/50 text-sm sm:text-base hover:border-white/40"
                  />
                </div>

                {/* Drop Location */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-white mb-2">
                    <MapPin className="h-4 w-4 text-primary" />
                    Drop Location
                  </label>
                  <input
                    type="text"
                    placeholder="Enter drop location"
                    value={destination}
                    onChange={(e) => setDestination(e.target.value)}
                    className="w-full px-4 py-3.5 rounded-xl bg-white/5 border border-white/20 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all duration-300 text-white placeholder-white/50 text-sm sm:text-base hover:border-white/40"
                  />
                </div>

                {/* Date & Time */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-white mb-2">
                    <Calendar className="h-4 w-4 text-primary" />
                    Date & Time
                  </label>
                  <input
                    type="datetime-local"
                    value={dateTime}
                    onChange={(e) => setDateTime(e.target.value)}
                    className="w-full px-4 py-3.5 rounded-xl bg-white/5 border border-white/20 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all duration-300 text-white text-sm sm:text-base hover:border-white/40 [color-scheme:dark]"
                  />
                </div>

                {/* Phone Number */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-white mb-2">
                    <Phone className="h-4 w-4 text-primary" />
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    placeholder="Enter phone number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full px-4 py-3.5 rounded-xl bg-white/5 border border-white/20 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all duration-300 text-white placeholder-white/50 text-sm sm:text-base hover:border-white/40"
                  />
                </div>

                {/* Vehicle Type */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-white mb-2">
                    <Car className="h-4 w-4 text-primary" />
                    Vehicle Type
                  </label>
                  <select
                    value={vehicleType}
                    onChange={(e) => setVehicleType(e.target.value)}
                    className="w-full px-4 py-3.5 rounded-xl bg-white/5 border border-white/20 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all duration-300 text-white text-sm sm:text-base appearance-none cursor-pointer hover:border-white/40"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%232196F3'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 12px center',
                      backgroundSize: '20px',
                    }}
                  >
                    {vehicles.map((vehicle) => (
                      <option key={vehicle.value} value={vehicle.value} className="bg-gray-900 text-white">
                        {vehicle.label}
                      </option>
                    ))}
                  </select>
                </div>



                {/* Book on WhatsApp Button */}
                <Button
                  type="button"
                  variant="whatsapp"
                  className="w-full font-bold py-4 sm:py-5 text-base mt-3 btn-premium touch-target-lg"
                  onClick={handleWhatsAppClick}
                >
                  Book on WhatsApp
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator - Desktop only */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 animate-bounce-subtle hidden lg:block">
          <a href="#vehicles" className="text-white/50 hover:text-primary transition-colors">
            <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
